import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { KeyRound, Loader2, CheckCircle2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { SITE } from "@/lib/site-config";
import Logo from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResetPassword = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => { document.title = `Passwort zurücksetzen · ${SITE.brand.name}`; }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) { toast.error("Passwort muss mindestens 8 Zeichen haben."); return; }
    if (pw !== pw2) { toast.error("Die Passwörter stimmen nicht überein."); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/admin/reset-password`, { token, new_password: pw }, { timeout: 15000 });
      setDone(true);
      toast.success("Passwort wurde zurückgesetzt.");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Zurücksetzen fehlgeschlagen.";
      toast.error(typeof detail === "string" ? detail : "Zurücksetzen fehlgeschlagen.");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#5C6B49] text-[#F3F1E8] flex items-center justify-center px-4" data-testid="reset-password-page">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="w-full max-w-md">
        <div className="flex justify-center mb-8"><Logo size={72} showWordmark={false} /></div>
        <div className="glass-strong rounded-2xl p-7 sm:p-9">
          {!token ? (
            <div className="text-center" data-testid="reset-invalid">
              <ShieldAlert size={40} className="text-[#E27D7D] mx-auto mb-4" />
              <h1 className="font-display text-2xl font-black tracking-tight mb-3">Ungültiger Link</h1>
              <p className="text-sm text-[#C9C6B4]">Dieser Link ist unvollständig. Bitte fordere einen neuen Link an.</p>
              <button onClick={() => navigate("/admin/forgot")} className="mt-6 w-full bg-[#E8E5D0] hover:bg-[#D6D3BF] text-[#3A4230] font-extrabold uppercase tracking-wider rounded-xl px-6 py-3 transition-all">Neuen Link anfordern</button>
            </div>
          ) : done ? (
            <div className="text-center" data-testid="reset-done">
              <CheckCircle2 size={40} className="text-[#B8CFA0] mx-auto mb-4" />
              <h1 className="font-display text-2xl font-black tracking-tight mb-3">Passwort gesetzt</h1>
              <p className="text-sm text-[#C9C6B4]">Dein Passwort wurde erfolgreich zurückgesetzt. Du kannst dich jetzt anmelden.</p>
              <button onClick={() => navigate("/admin/login")} className="mt-6 w-full bg-[#E8E5D0] hover:bg-[#D6D3BF] text-[#3A4230] font-extrabold uppercase tracking-wider rounded-xl px-6 py-3 transition-all" data-testid="reset-to-login-btn">Zum Login</button>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 mb-1">
                <KeyRound size={14} className="text-[#E8E5D0]" />
                <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#E8E5D0]">Neues Passwort</span>
              </div>
              <h1 className="font-display text-2xl sm:text-3xl font-black tracking-tight leading-tight mb-2">Passwort zurücksetzen</h1>
              <p className="text-sm text-[#C9C6B4] mb-6">Wähle ein neues, sicheres Passwort (mindestens 8 Zeichen).</p>
              <form onSubmit={onSubmit} className="space-y-4" data-testid="reset-password-form">
                <input type="password" autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} required placeholder="Neues Passwort"
                  className="w-full bg-[#4E5A3B]/60 border border-white/15 focus:border-[#E8E5D0] focus:ring-2 focus:ring-[#E8E5D0]/25 outline-none rounded-lg px-4 py-3 text-[#F3F1E8] text-[15px] placeholder:text-[#A8A58F]" data-testid="reset-pw1" />
                <input type="password" autoComplete="new-password" value={pw2} onChange={(e) => setPw2(e.target.value)} required placeholder="Passwort wiederholen"
                  className="w-full bg-[#4E5A3B]/60 border border-white/15 focus:border-[#E8E5D0] focus:ring-2 focus:ring-[#E8E5D0]/25 outline-none rounded-lg px-4 py-3 text-[#F3F1E8] text-[15px] placeholder:text-[#A8A58F]" data-testid="reset-pw2" />
                <button type="submit" disabled={loading}
                  className="w-full bg-[#E8E5D0] hover:bg-[#D6D3BF] disabled:opacity-50 text-[#3A4230] font-extrabold uppercase tracking-wider rounded-xl px-6 py-3.5 flex items-center justify-center gap-2 transition-all" data-testid="reset-submit-btn">
                  {loading ? <><Loader2 className="animate-spin" size={16} /> Speichern…</> : <>Passwort speichern</>}
                </button>
              </form>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default ResetPassword;
